import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { CoreHttpClient, CurrencySelector } from "@obg/ngx.core";
import { CoreContentState, TranslationService } from "@obg/ngx.core.content";
import { CurrencyService, DateFormat, DateService } from "@obg/ngx.global";
import { ignoreNil } from "@obg/rxjs";
import { Observable } from "rxjs";
import { filter, map, switchMap, take, shareReplay } from "rxjs/operators";

import { CommonState } from "../../state";
import { ResponsibleGamingUiConfigService } from "../config";

import { BettingLimitConfigService } from "./betting-limit.config";
import { BETTING_LIMIT_PERIODS, BETTING_LIMIT_PERIODS_TYPE } from "./betting-limit.const";
import {
	BettingActiveLimit,
	BettingLimit,
	BettingLimitActive,
	BettingLimitConfig,
	BettingLimitForm,
	BettingLimitParams,
	BettingLimitPeriod,
	BettingLimitStatus,
	BettingLimitType,
	NetLossLimit,
	NetLossLimitConfirmation,
	Prefix,
	TimedBettingLimitParams,
	WageringLimitParams,
} from "./betting-limit.model";
import { BettingLimitSelector } from "./betting-limit.selector";
import { selectBetLimitId } from "./netloss-limit-confirmation/netloss-limit-confirmation.selector";

@Injectable({
	providedIn: "root",
})
export class BettingLimitService {
	jurisdictionTranslatePrefix: string | undefined;

	constructor(
		private httpClient: CoreHttpClient,
		private config: BettingLimitConfigService,
		private store: Store<CommonState & CoreContentState>,
		private responsibleGamingUiConfigService: ResponsibleGamingUiConfigService,
		private currencySelector: CurrencySelector,
		private translationService: TranslationService,
		private dateService: DateService,
		private currencyService: CurrencyService
	) {}

	getBetCountLimits(): Observable<BettingLimit[]> {
		return this.httpClient
			.get<BettingLimit[]>({
				url: this.config.getBetCountLimitEndpoint(),
				searchParams: {
					betLimitState: BettingLimitStatus.All,
				},
			})
			.pipe(
				map((bettingLimits) => {
					const activeAndQueuedBettingLimits = this.getActiveAndQueuedBettingLimits(bettingLimits);
					this.updateBettingLimitType(activeAndQueuedBettingLimits, BettingLimitType.BetCountLimit);
					return activeAndQueuedBettingLimits;
				})
			);
	}

	getBetSizeLimits(): Observable<BettingLimit[]> {
		return this.httpClient
			.get<BettingLimit[]>({
				url: this.config.getBetSizeLimitEndpoint(),
				searchParams: {
					betLimitState: BettingLimitStatus.All,
				},
			})
			.pipe(
				map((bettingLimits) => {
					const activeAndQueuedBettingLimits = this.getActiveAndQueuedBettingLimits(bettingLimits);
					this.updateBettingLimitType(activeAndQueuedBettingLimits, BettingLimitType.BetSizeLimit);
					return activeAndQueuedBettingLimits;
				})
			);
	}

	getWageringLimits(): Observable<BettingLimit[]> {
		return this.httpClient
			.get<BettingLimit[]>({
				url: this.config.getWageringLimitEndpoint(),
				searchParams: {
					limitState: BettingLimitStatus.All,
				},
			})
			.pipe(
				map((bettingLimits) => {
					const activeAndQueuedBettingLimits = this.getActiveAndQueuedBettingLimits(bettingLimits);
					this.updateBettingLimitType(activeAndQueuedBettingLimits, BettingLimitType.WageringLimit);
					return activeAndQueuedBettingLimits;
				})
			);
	}

	getTimedWageringLimits(): Observable<BettingLimit[]> {
		return this.httpClient
			.get<BettingLimit[]>({
				url: this.config.getBetLimitEndpoint(),
				searchParams: {
					betLimitType: BettingLimitType.WageringLimit,
					betLimitState: BettingLimitStatus.All,
				},
			})
			.pipe(
				map((bettingLimits) => {
					const activeAndQueuedBettingLimits = this.getActiveAndQueuedBettingLimits(bettingLimits);
					this.updateBettingLimitType(activeAndQueuedBettingLimits, BettingLimitType.TimedWageringLimit);
					return activeAndQueuedBettingLimits;
				})
			);
	}

	getNetlossLimits(): Observable<BettingLimit[]> {
		return this.httpClient
			.get<BettingLimit[]>({
				url: this.config.getBetLimitEndpoint(),
				searchParams: {
					betLimitType: BettingLimitType.NetlossLimit,
					betLimitState: BettingLimitStatus.All,
				},
			})
			.pipe(
				map((bettingLimits) => {
					const activeAndQueuedBettingLimits = this.getActiveAndQueuedBettingLimits(bettingLimits);
					this.updateBettingLimitType(activeAndQueuedBettingLimits, BettingLimitType.NetlossLimit);
					return activeAndQueuedBettingLimits;
				})
			);
	}

	getNetLossLimitConfirmation(): Observable<NetLossLimit> {
		return this.httpClient
			.get<NetLossLimit[]>({
				url: this.config.getBetLimitEndpoint(),
				searchParams: {
					betLimitType: BettingLimitType.NetlossLimit,
					betLimitState: BettingLimitStatus.WaitingForConfirmation,
				},
			})
			.pipe(map(([netLoassConfirmation]) => netLoassConfirmation));
	}

	getRemainingBetLimit(): Observable<BettingLimit[]> {
		return this.httpClient
			.get<BettingLimit[]>({ url: this.config.getRemainingBetLimitEndPoint() })
			.pipe(map((bettingLimits) => bettingLimits));
	}

	getAllTimedWageringLimits(): Observable<BettingLimit[]> {
		return this.httpClient
			.get<BettingLimit[]>({
				url: this.config.getBetLimitEndpoint(),
				searchParams: {
					betLimitType: BettingLimitType.WageringLimit,
					betLimitState: BettingLimitStatus.All,
				},
			})
			.pipe(map((bettingLimits) => bettingLimits));
	}

	getAllNetlossLimits(): Observable<BettingLimit[]> {
		return this.httpClient
			.get<BettingLimit[]>({
				url: this.config.getBetLimitEndpoint(),
				searchParams: {
					betLimitType: BettingLimitType.NetlossLimit,
					betLimitState: BettingLimitStatus.All,
				},
			})
			.pipe(map((bettingLimits) => bettingLimits));
	}

	updateNetLossLimitConfirmation(data: NetLossLimitConfirmation): Observable<void> {
		return this.store.select(selectBetLimitId).pipe(
			ignoreNil(),
			take(1),
			switchMap((betLimitId) =>
				this.httpClient.patch<void>({
					url: `${this.config.getBetLimitEndpoint()}/${betLimitId}`,
					data,
				})
			)
		);
	}

	updateBetCountLimit(params: BettingLimitParams): Observable<BettingLimit> {
		return this.httpClient.put<BettingLimit>({
			url: this.config.getBetCountLimitEndpoint(),
			data: params,
		});
	}

	updateBetSizeLimit(params: BettingLimitParams): Observable<BettingLimit> {
		return this.httpClient.put<BettingLimit>({
			url: this.config.getBetSizeLimitEndpoint(),
			data: params,
		});
	}

	updateWageringLimit(params: WageringLimitParams): Observable<BettingLimit> {
		return this.httpClient.put<BettingLimit>({
			url: this.config.getWageringLimitEndpoint(),
			data: params,
		});
	}

	updateTimedWageringLimit(params: TimedBettingLimitParams): Observable<BettingLimit> {
		return this.httpClient.put<BettingLimit>({
			url: this.config.getTimedWageringLimitUpdateEndpoint(),
			data: params,
		});
	}

	updateNetlossLimit(params: TimedBettingLimitParams): Observable<BettingLimit> {
		return this.httpClient.put<BettingLimit>({
			url: this.config.getNetlossLimitUpdateEndpoint(),
			data: params,
		});
	}

	removeBetCountLimit(): Observable<BettingLimit> {
		return this.httpClient.delete<BettingLimit>({
			url: `${this.config.getBetCountLimitEndpoint()}`,
		});
	}

	removeBetSizeLimit(): Observable<BettingLimit> {
		return this.httpClient.delete<BettingLimit>({
			url: `${this.config.getBetSizeLimitEndpoint()}`,
		});
	}

	removeWageringLimit(): Observable<BettingLimit> {
		return this.httpClient.delete<BettingLimit>({
			url: `${this.config.getWageringLimitEndpoint()}`,
		});
	}

	removeTimedWageringLimit(): Observable<BettingLimit> {
		return this.httpClient.delete<BettingLimit>({
			url: `${this.config.getTimedWageringLimitUpdateEndpoint()}`,
		});
	}

	removeTimedWageringLimitWithPayload(params: TimedBettingLimitParams): Observable<BettingLimit> {
		return this.httpClient.delete<BettingLimit>({
			url: `${this.config.getTimedWageringLimitUpdateEndpoint()}`,
			data: params,
		});
	}

	removeNetlossLimit(): Observable<BettingLimit> {
		return this.httpClient.delete<BettingLimit>({
			url: `${this.config.getNetlossLimitUpdateEndpoint()}`,
		});
	}

	removeNetlossLimitWithPayload(params: TimedBettingLimitParams): Observable<BettingLimit> {
		return this.httpClient.delete<BettingLimit>({
			url: `${this.config.getNetlossLimitUpdateEndpoint()}`,
			data: params,
		});
	}

	getActiveBettingLimit$(): Observable<BettingActiveLimit | undefined> {
		return this.store.pipe(select(BettingLimitSelector.getActiveBettingLimit)).pipe(
			switchMap((activeLimit: BettingLimitActive) => {
				const limitType = this.getBettingLimitType(activeLimit);

				return this.translationService
					.translateByJurisdictionPrefixList$({
						defaultCrsKeys: [
							`${Prefix.BettingLimit}.upcoming-deletion`,
							`${Prefix.BettingLimit}.${limitType}`,
						],
						interpolateParams: {
							endDate: this.dateService.format(activeLimit?.endDate, DateFormat.ShortDate),
							amount: this.currencyService.format(activeLimit?.limitValue),
						},
						dotMapping: true,
					})
					.pipe(
						map(([limitInfo, cashAmount]) => ({
							data: activeLimit,
							limitInfo,
							cashAmount,
						}))
					);
			})
		);
	}

	getBettingLimitComponentConfig(): BettingLimitConfig | undefined {
		return {
			isDeleteLimitEnabled: this.responsibleGamingUiConfigService.getShowBettingLimitDeleteBtn(),
		};
	}

	getBettingLimitContent$(
		dateLimitType: BettingLimitType = BettingLimitType.WageringLimit
	): Observable<any | undefined> {
		const keys = [
			`${Prefix.BettingLimit}.current-limit`,
			`${Prefix.BettingLimit}.limit-none`,
			`${Prefix.BettingLimit}.title`,
			`${Prefix.BettingLimit}.edit-betting-limit`,
			`${Prefix.BettingLimit}.delete-betting-limit`,
			`${Prefix.BettingLimit}.edit-your-limit`,
			`${Prefix.BettingLimit}.limit-${dateLimitType}`,
			`${Prefix.BettingLimit}.cancel-edit-betting-limit`,
			`${Prefix.BettingLimit}.save-betting-limit`,
			`${Prefix.BettingLimit}.caption`,
			`${Prefix.BettingLimit}.amount.required`,
			`${Prefix.BettingLimit}.amount.maxlength`,
			`${Prefix.BettingLimit}.queued-limit`,
			`${Prefix.BettingLimit}.queued-limit.bets`,
			`${Prefix.BettingLimit}.queued-bet-size-limit`,
			`${Prefix.BettingLimit}.queued-limit.wagering`,
			`${Prefix.BettingLimit}.queued-limit.timed-wagering.daily`,
			`${Prefix.BettingLimit}.queued-limit.timed-wagering.monthly`,
			`${Prefix.BettingLimit}.queued-limit.timed-wagering.weekly`,
			`${Prefix.BettingLimit}.upcoming-deletion`,
			`currency`,
		];

		return this.getCurrencyCode$().pipe(
			ignoreNil(),
			switchMap((currency: string) =>
				this.translationService
					.translateByJurisdictionPrefixList$({
						defaultCrsKeys: keys,
						interpolateParams: {
							currency,
						},
						dotMapping: true,
					})
					.pipe(
						map(
							([
								currentLimit,
								limitNone,
								title,
								buttonEdit,
								buttonDelete,
								editTitle,
								inputLabel,
								buttonCancel,
								buttonSave,
								caption,
								required,
								maxLength,
								queuedLimit,
								queuedLimitΒets,
								queuedLimitBetSizeLimit,
								queuedLimitBetSizeWagering,
								queuedLimitBetSizeTimedWageringDaily,
								queuedLimitBetSizeTimedWageringMonthly,
								queuedLimitBetSizeTimedWageringWeekly,
								upcomingDeletion,
							]) => ({
								currentLimit,
								limitNone,
								title,
								buttonEdit,
								buttonDelete,
								editTitle,
								inputLabel,
								buttonCancel,
								buttonSave,
								caption,
								required,
								maxLength,
								queuedLimit,
								queuedLimitΒets,
								queuedLimitBetSizeLimit,
								queuedLimitBetSizeWagering,
								queuedLimitBetSizeTimedWageringDaily,
								queuedLimitBetSizeTimedWageringMonthly,
								queuedLimitBetSizeTimedWageringWeekly,
								upcomingDeletion,
								currency,
							})
						)
					)
			)
		);
	}

	getbettingQueuedLimit$(): Observable<BettingLimit | undefined> {
		return this.store.pipe(select(BettingLimitSelector.getQueuedBettingLimit), shareReplay(1));
	}

	getBettingLimitPeriods$(): Observable<BettingLimitPeriod[] | undefined> | undefined {
		return this.translationService
			.translateByJurisdictionPrefixList$({ defaultCrsKeys: BETTING_LIMIT_PERIODS.map((res) => res.label) })
			.pipe(
				map((translations) =>
					BETTING_LIMIT_PERIODS.map((period, index) => ({
						value: period.value,
						label: translations[index] || "",
						isChecked: false,
					}))
				),
				filter((translations) => !!translations.length),
				map((translations) => {
					translations[0].isChecked = true;
					return translations;
				})
			);
	}

	getbettingLimitType$(): Observable<BettingLimitPeriod[] | undefined> | undefined {
		return this.translationService
			.translateByJurisdictionPrefixList$({
				defaultCrsKeys: BETTING_LIMIT_PERIODS_TYPE.map((res) => res.label),
				dotMapping: true,
			})
			.pipe(
				map((translations) =>
					BETTING_LIMIT_PERIODS_TYPE.map((period, index) => ({
						value: period.value,
						label: translations[index] || "",
						isChecked: false,
					}))
				),
				map((translations) => {
					translations[0].isChecked = true;
					return translations;
				})
			);
	}

	getCurrencyCode$(): Observable<string | undefined> {
		return this.store.select(this.currencySelector.getByCurrentCustomer()).pipe(map((currency) => currency?.code));
	}

	getInitialForm(): BettingLimitForm {
		return {
			limit: {
				isRequired: true,
				maxLength: 12,
				value: undefined,
			},
			limitType: BettingLimitType.WageringLimit,
		};
	}

	private getActiveAndQueuedBettingLimits(bettingLimits: BettingLimit[]): BettingLimit[] {
		return bettingLimits.filter(
			(limit) => limit.status === BettingLimitStatus.Active || limit.status === BettingLimitStatus.Queued
		);
	}

	private updateBettingLimitType(bettingLimits: BettingLimit[], bettingLimitType: BettingLimitType): void {
		bettingLimits.forEach((limit) => (limit.type = bettingLimitType));
	}

	private getBettingLimitType(activeLimit: BettingLimitActive): string {
		let limitType;

		switch (activeLimit.type) {
			case BettingLimitType.BetCountLimit:
				limitType = "bets-per-day";
				break;
			case BettingLimitType.BetSizeLimit:
				limitType = "cash-per-bet";
				break;
			case BettingLimitType.WageringLimit:
				limitType = "cash-per-day";
				break;
			case BettingLimitType.TimedWageringLimit:
				limitType = "cash-per-period";
				break;
			default:
				limitType = "limit-none";
		}
		return limitType;
	}
}
